import { useState, useEffect, type KeyboardEvent } from 'react';
import { supabase } from '@/lib/supabase';
import { parseEmailList, isLikelyEmail } from '@/lib/notifications';

interface ContactoBase {
  id: number;
  client_id: number;
  department: string | null;
  phone: string | null;
  email: string | null;
}

interface BillingContactsCardProps {
  clientId: number;
  contacts: ContactoBase[];
  onSaved: () => void;
}

// El contacto de cobranza se identifica por su departamento 'Cobranza'.
// Reutiliza la tabla existente de contactos: guarda el WhatsApp en `phone`
// y la lista de correos (separada por comas) en `email`, sin agregar columnas.
const BILLING_DEPARTMENT = 'Cobranza';

export default function BillingContactsCard({ clientId, contacts, onSaved }: BillingContactsCardProps) {
  const cobranzaContact = contacts.find((c) => c.department === BILLING_DEPARTMENT) || null;

  const [whatsapp, setWhatsapp] = useState('');
  const [emails, setEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    setWhatsapp(cobranzaContact?.phone || '');
    setEmails(parseEmailList(cobranzaContact?.email || ''));
    setEmailInput('');
    setError('');
    setSuccess('');
  }, [cobranzaContact?.id, cobranzaContact?.phone, cobranzaContact?.email]);

  function addEmailsFromInput() {
    const parsed = parseEmailList(emailInput);
    if (parsed.length === 0) return;
    setEmails((prev) => {
      const seen = new Set(prev.map((e) => e.toLowerCase()));
      const next = [...prev];
      parsed.forEach((e) => {
        if (!seen.has(e.toLowerCase())) {
          seen.add(e.toLowerCase());
          next.push(e);
        }
      });
      return next;
    });
    setEmailInput('');
    setError('');
  }

  function removeEmail(email: string) {
    setEmails((prev) => prev.filter((e) => e !== email));
  }

  function handleEmailKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
      e.preventDefault();
      addEmailsFromInput();
    } else if (e.key === 'Backspace' && emailInput === '' && emails.length > 0) {
      setEmails((prev) => prev.slice(0, -1));
    }
  }

  async function handleSave() {
    setError('');
    setSuccess('');

    const cleanWhatsApp = whatsapp.replace(/[\s\-\(\)]/g, '');
    if (cleanWhatsApp && cleanWhatsApp.replace(/\+/g, '').length < 10) {
      setError('El Número de WhatsApp debe tener al menos 10 dígitos.');
      return;
    }

    const finalEmails = (() => {
      const parsed = parseEmailList(emailInput);
      const seen = new Set(emails.map((e) => e.toLowerCase()));
      const next = [...emails];
      parsed.forEach((e) => {
        if (!seen.has(e.toLowerCase())) {
          seen.add(e.toLowerCase());
          next.push(e);
        }
      });
      return next;
    })();

    const invalid = finalEmails.find((e) => !isLikelyEmail(e));
    if (invalid) {
      setError(`El correo "${invalid}" no tiene un formato válido.`);
      return;
    }

    setSaving(true);
    try {
      const payload = {
        phone: whatsapp.trim() || null,
        email: finalEmails.join(', ') || null,
      };

      if (cobranzaContact) {
        const { error: updErr } = await supabase
          .from('erp_contacts')
          .update(payload)
          .eq('id', cobranzaContact.id);
        if (updErr) throw updErr;
      } else {
        const { error: insErr } = await supabase
          .from('erp_contacts')
          .insert({
            client_id: clientId,
            name: 'Contacto de Cobranza',
            position: BILLING_DEPARTMENT,
            department: BILLING_DEPARTMENT,
            phone: payload.phone,
            email: payload.email,
          });
        if (insErr) throw insErr;
      }

      setEmailInput('');
      setEmails(finalEmails);
      setSuccess('Datos de cobranza guardados.');
      onSaved();
      setTimeout(() => setSuccess(''), 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar los datos de cobranza.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-white rounded-xl border border-background-200 p-5">
      <div className="flex items-center gap-2 mb-1">
        <div className="w-8 h-8 rounded-lg bg-secondary-100 flex items-center justify-center">
          <i className="ri-notification-3-line text-secondary-700 text-sm"></i>
        </div>
        <div>
          <h3 className="text-sm font-semibold text-foreground-900">Datos de Contacto para Cobranza</h3>
          <p className="text-[11px] text-foreground-500">Se usan para enviar facturas por WhatsApp y correo.</p>
        </div>
      </div>

      <div className="space-y-4 mt-4">
        {success && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2">
            <i className="ri-checkbox-circle-fill text-emerald-500"></i>
            <p className="text-xs text-emerald-700 font-medium">{success}</p>
          </div>
        )}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
            <i className="ri-error-warning-fill text-red-500"></i>
            <p className="text-xs text-red-700 font-medium">{error}</p>
          </div>
        )}

        <div>
          <label className="block text-xs font-semibold text-foreground-700 mb-1">Número de WhatsApp</label>
          <div className="relative">
            <i className="ri-whatsapp-line absolute left-3 top-1/2 -translate-y-1/2 text-green-600 text-sm"></i>
            <input
              type="tel"
              value={whatsapp}
              onChange={(e) => { setWhatsapp(e.target.value); setError(''); }}
              className="w-full pl-9 pr-3 py-2 text-sm border border-background-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 text-foreground-800"
              placeholder="Ej. +52 1 55 1234 5678"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-foreground-700 mb-1">Correos Electrónicos</label>
          <div className="flex flex-wrap items-center gap-1.5 w-full px-2.5 py-2 border border-background-200 rounded-lg bg-white focus-within:border-primary-400 focus-within:ring-2 focus-within:ring-primary-100">
            {emails.map((email) => (
              <span key={email} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium">
                {email}
                <button
                  type="button"
                  onClick={() => removeEmail(email)}
                  className="w-4 h-4 rounded-full hover:bg-black/10 flex items-center justify-center cursor-pointer"
                  title="Quitar correo"
                >
                  <i className="ri-close-line text-[10px]"></i>
                </button>
              </span>
            ))}
            <input
              type="text"
              value={emailInput}
              onChange={(e) => setEmailInput(e.target.value)}
              onKeyDown={handleEmailKeyDown}
              onBlur={addEmailsFromInput}
              placeholder={emails.length === 0 ? 'correo@cliente.com' : 'Agregar otro correo...'}
              className="flex-1 min-w-[140px] px-1 py-0.5 text-sm bg-transparent focus:outline-none text-foreground-800 placeholder:text-foreground-400"
            />
          </div>
          <p className="text-[10px] text-foreground-500 mt-1">Escriba y presione Enter o coma para agregar varias direcciones.</p>
        </div>

        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-xs font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 cursor-pointer transition-colors whitespace-nowrap flex items-center gap-1.5 disabled:opacity-50"
          >
            <i className={saving ? 'ri-loader-4-line animate-spin' : 'ri-save-line'}></i>
            {saving ? 'Guardando...' : 'Guardar Datos de Cobranza'}
          </button>
        </div>
      </div>
    </div>
  );
}